import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import {
  debounce,
  isMobileBreakpoint,
  mediaQueryCartDrawerShort,
  prefersReducedMotion,
  ResizeNotifier,
} from '@theme/utilities';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';
import { scrollIntoView } from '@theme/scrolling';

/** Milliseconds to wait before opening the drawer optimistically when auto-open is enabled. */
const AUTO_OPEN_DELAY = 150;

/** @type {string} */
const CART_DRAWER_LAYOUT_SYNC_EVENT = 'cart-drawer:layout-sync';

/** Theme-private signal dispatched by cart producers right before a section morph so the drawer can restore upsell to its home slot. */
const CART_DRAWER_PRE_MORPH_EVENT = 'cart-drawer:pre-morph';

/** Theme-private signal dispatched when cart drawer empty-state markup changes without a section fetch. */
const CART_DRAWER_EMPTY_STATE_SYNC_EVENT = 'cart-drawer:sync-empty-state';

/** @type {string} */
const CART_UPSELL_SELECTOR = 'accordion-custom.cart-upsell[data-cart-upsell]';

/** @type {string} */
const CART_UPSELL_HOME_SELECTOR = '[data-cart-upsell-home]';

/**
 * A custom element that manages cart-specific behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, focus trap, session restore) is owned by
 * `<theme-drawer>`. This component keeps the Bonshore cart UX: optimistic auto-open,
 * the pending bar + empty-state status messages, the reward/upsell layout sync
 * (sticky summary, scroll hints, short-viewport upsell relocation, offers-below cue)
 * and mobile back-to-close.
 *
 * Cart count announcements are owned by `<header-actions>`.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} Ratio above which the summary stops being sticky. */
  #summaryThreshold = 0.5;

  /** @type {ResizeNotifier | null} */
  #layoutObserver = null;

  /** @type {AbortController | null} */
  #viewportLayoutAbortController = null;

  /** @type {AbortController | null} */
  #offersCueAbortController = null;

  /** @type {number | null} */
  #layoutSyncRafId = null;

  /** @type {number | null} */
  #offersCueRafId = null;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {boolean} Tracks whether this open cycle pushed a history entry. */
  #didPushHistoryEntry = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #pendingTimer = null;

  /** @type {number} Tracks in-flight add-to-cart requests to handle rapid multi-click correctly. */
  #pendingCount = 0;

  /** @type {boolean} True when the drawer was opened before the cart response arrived. */
  #isOptimisticallyOpen = false;

  /** @type {number} Tracks in-flight discount update requests. */
  #discountPendingCount = 0;

  /** @type {number} Tracks in-flight cart quantity update and removal requests. */
  #cartUpdatePendingCount = 0;

  /** @type {number} Tracks in-flight cart clear/remove requests. */
  #cartUpdateClearPendingCount = 0;

  /** @type {ReturnType<typeof debounce> | null} */
  #debouncedViewportLayoutSync = null;

  /** @type {MutationObserver | null} Watches drawer content swaps to keep pending empty-state copy in sync. */
  #pendingStatusObserver = null;

  /** @type {AbortController | null} Keeps in-slot upsell open as a static panel. */
  #upsellStaticPanelAbortController = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  /** @returns {boolean} Whether the parent theme-drawer is open. */
  get #isDrawerOpen() {
    return Boolean(this.#themeDrawer?.hasAttribute('open'));
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleCartDiscountUpdate);
    document.addEventListener(CART_DRAWER_PRE_MORPH_EVENT, this.#handlePreMorph, { capture: true });
    this.addEventListener(CART_DRAWER_LAYOUT_SYNC_EVENT, this.#handleLayoutSyncRequest);
    this.addEventListener(CART_DRAWER_EMPTY_STATE_SYNC_EVENT, this.#handleEmptyStateSyncRequest);

    const themeDrawer = this.#themeDrawer;
    themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    themeDrawer?.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);

    this.#setupPendingStatusObserver();

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleCartDiscountUpdate);
    document.removeEventListener(CART_DRAWER_PRE_MORPH_EVENT, this.#handlePreMorph, { capture: true });
    this.removeEventListener(CART_DRAWER_LAYOUT_SYNC_EVENT, this.#handleLayoutSyncRequest);
    this.removeEventListener(CART_DRAWER_EMPTY_STATE_SYNC_EVENT, this.#handleEmptyStateSyncRequest);

    const themeDrawer = this.#themeDrawer;
    themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    themeDrawer?.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);

    this.#disconnectLayoutTracking();
    this.#pendingStatusObserver?.disconnect();
    this.#pendingStatusObserver = null;
    this.#historyAbortController?.abort();
    this.#didPushHistoryEntry = false;
    if (this.#pendingTimer !== null) {
      clearTimeout(this.#pendingTimer);
      this.#pendingTimer = null;
    }
  }

  /**
   * Runs when the theme-drawer opens: wires history, syncs empty-state, sets up
   * layout tracking (sticky summary, scroll hints, upsell placement) and the
   * installments close-on-click.
   */
  #handleDrawerOpen = () => {
    this.#handleHistoryOpen();
    this.#syncDialogEmptyState();
    this.#disconnectLayoutTracking();
    this.#scheduleDrawerLayoutSync();
    this.#ensureLayoutObserver();
    this.#bindViewportLayoutListeners();
    this.#bindOffersCue();
    this.#wireInstallmentsClose();
  };

  #handleDrawerClose = () => {
    this.#handleHistoryClose();
    this.#disconnectLayoutTracking();
    this.#hideOffersCue();
  };

  /**
   * Close cart drawer when the installments CTA is clicked to avoid overlapping dialogs.
   * Re-queried on every open so it survives cart content re-renders that replace the
   * shopify-payment-terms shadow root.
   */
  #wireInstallmentsClose() {
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;
    if (this.#historyAbortController) return;

    this.#didPushHistoryEntry = true;
    history.pushState({ ...history.state, cartDrawerOpen: true }, '');

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    if (!this.#historyAbortController && !this.#didPushHistoryEntry) return;

    const bodyTop = Number.parseInt(document.body.style.top || '0', 10);
    const preservedScrollY =
      document.body.style.position === 'fixed' && Number.isFinite(bodyTop) ? Math.abs(bodyTop) : window.scrollY;

    this.#historyAbortController?.abort();
    this.#historyAbortController = null;

    if (this.#didPushHistoryEntry) {
      this.#didPushHistoryEntry = false;
      const previousScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
      history.back();

      requestAnimationFrame(() => {
        window.scrollTo({ top: preservedScrollY, behavior: 'instant' });
        history.scrollRestoration = previousScrollRestoration;
      });
    }
  };

  #handlePopState = async () => {
    this.#didPushHistoryEntry = false;

    if (this.#isDrawerOpen) {
      await this.#themeDrawer?.close();
    }
  };

  /**
   * Handles a standard `CartLinesUpdateEvent`. The synchronous phase drives optimistic auto-open
   * (add) and the pending bar (update/remove); the promise phase reconciles once the request settles.
   * @param {Event} event
   */
  #handleCartLinesUpdate = (event) => {
    const linesEvent = /** @type {import('@shopify/events').CartLinesUpdateEvent} */ (event);
    const action = linesEvent.action;

    // Synchronous phase — pending bar for add (optimistic open) and quantity update only.
    // Lightweight removes skip the bar; optimistic row UI handles that path.
    if (action === 'add') {
      this.#beginAddPending();
    } else if (action === 'update') {
      this.#cartUpdatePendingCount++;
      this.#showPendingBar();
    }

    const promise = linesEvent.promise;
    if (!promise) {
      if (action === 'update') this.#endCartUpdatePending(action);
      return;
    }

    promise
      .then((result) => {
        const detail = /** @type {any} */ (result)?.detail;
        if (action === 'add') {
          this.#handleCartAddSettled(detail);
        } else if (action === 'update') {
          this.#endCartUpdatePending(action);
        }
        this.#applyLayoutAfterCartUpdate(detail);
      })
      .catch((error) => {
        if (action === 'add') {
          this.#handleCartAddSettled({ didError: true });
        } else if (action === 'update') {
          this.#endCartUpdatePending(action);
        }
        if (error?.name !== 'AbortError') {
          this.#applyLayoutAfterCartUpdate({ didError: true });
        }
      });
  };

  /**
   * Handles a standard `CartDiscountUpdateEvent`: shows the pending bar synchronously and
   * clears it plus re-syncs layout once the promise settles.
   * @param {Event} event
   */
  #handleCartDiscountUpdate = (event) => {
    const discountEvent = /** @type {import('@shopify/events').CartDiscountUpdateEvent} */ (event);

    this.#discountPendingCount++;
    this.#showPendingBar();

    const promise = discountEvent.promise;
    if (!promise) {
      this.#discountPendingCount = Math.max(0, this.#discountPendingCount - 1);
      this.#hidePendingBarIfIdle();
      return;
    }

    promise
      .then((result) => {
        this.#discountPendingCount = Math.max(0, this.#discountPendingCount - 1);
        this.#hidePendingBarIfIdle();
        this.#applyLayoutAfterCartUpdate(/** @type {any} */ (result)?.detail);
      })
      .catch(() => {
        this.#discountPendingCount = Math.max(0, this.#discountPendingCount - 1);
        this.#hidePendingBarIfIdle();
      });
  };

  /** Schedules an optimistic auto-open for an add-to-cart request (replaces CartAddPendingEvent). */
  #beginAddPending() {
    if (!this.hasAttribute('auto-open')) return;
    // Drawer already open — nothing to optimise.
    if (this.#isDrawerOpen) return;

    this.#pendingCount++;

    // Restart the delay timer on each rapid click so only one open is scheduled.
    if (this.#pendingTimer !== null) clearTimeout(this.#pendingTimer);
    this.#pendingTimer = setTimeout(() => {
      this.#pendingTimer = null;
      if (!this.#isDrawerOpen) {
        this.#isOptimisticallyOpen = true;
        this.#showPendingBar();
        this.#themeDrawer?.open();
      }
    }, AUTO_OPEN_DELAY);
  }

  /**
   * Reconciles auto-open and the pending bar when an add-to-cart request settles.
   * @param {{ didError?: boolean } | undefined} detail
   */
  #handleCartAddSettled(detail) {
    // Cancel a delayed open that hasn't fired yet — the response was fast enough.
    if (this.#pendingTimer !== null) {
      clearTimeout(this.#pendingTimer);
      this.#pendingTimer = null;
    }

    this.#pendingCount = Math.max(0, this.#pendingCount - 1);

    if (detail?.didError) {
      // Close a drawer that was opened optimistically if no other requests are pending.
      if (this.#isOptimisticallyOpen && this.#pendingCount === 0) {
        this.#isOptimisticallyOpen = false;
        this.#hidePendingBarIfIdle();
        this.#themeDrawer?.close();
      }
      return;
    }

    // Success — clear optimistic flag immediately so a subsequent error response
    // from a different in-flight request cannot close a drawer that was legitimately opened.
    this.#isOptimisticallyOpen = false;
    if (this.#pendingCount === 0) {
      this.#hidePendingBarIfIdle();
    }

    // If the drawer hasn't opened yet (fast-path: response arrived before the delay timer),
    // open it now as usual.
    if (this.hasAttribute('auto-open') && !this.#isDrawerOpen) {
      this.#themeDrawer?.open();
    }
  }

  /**
   * Clears the pending bar bookkeeping for a settled quantity update / removal request.
   * @param {'update' | 'remove'} action
   */
  #endCartUpdatePending(action) {
    this.#cartUpdatePendingCount = Math.max(0, this.#cartUpdatePendingCount - 1);
    if (action === 'remove') {
      this.#cartUpdateClearPendingCount = Math.max(0, this.#cartUpdateClearPendingCount - 1);
    }
    this.#hidePendingBarIfIdle();
  }

  #showPendingBar() {
    this.setAttribute('data-cart-pending', '');
    this.setAttribute('data-cart-pending-context', this.#getPendingContext());
    this.#syncPendingEmptyStatusMessage();
    // Empty-cart content can be injected after pending starts (for example when
    // removing the last item). Sync again on the next frame so the correct
    // pending message is revealed once that content exists.
    requestAnimationFrame(() => {
      if (!this.hasAttribute('data-cart-pending')) return;
      this.#syncPendingEmptyStatusMessage();
    });
  }

  #hidePendingBarIfIdle() {
    if (this.#pendingCount > 0 || this.#isOptimisticallyOpen || this.#discountPendingCount > 0 || this.#cartUpdatePendingCount > 0) return;
    this.removeAttribute('data-cart-pending');
    this.removeAttribute('data-cart-pending-context');
    this.#syncPendingEmptyStatusMessage();
  }

  #getPendingContext() {
    if (this.#pendingCount > 0 || this.#isOptimisticallyOpen) return 'add';
    if (this.#cartUpdateClearPendingCount > 0) return 'remove';
    return 'update';
  }

  #syncPendingEmptyStatusMessage() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const addingMessage = dialog.querySelector('.cart-drawer__empty-status-message--pending-adding');
    const removingMessage = dialog.querySelector('.cart-drawer__empty-status-message--pending-removing');

    if (!(addingMessage instanceof HTMLElement) || !(removingMessage instanceof HTMLElement)) return;

    const shouldShowRemoving = this.getAttribute('data-cart-pending-context') === 'remove';
    addingMessage.toggleAttribute('hidden', shouldShowRemoving);
    removingMessage.toggleAttribute('hidden', !shouldShowRemoving);
  }

  /**
   * Keeps the empty-state class and dialog label in sync after cart content morphs.
   * @param {number | undefined} [itemCount]
   */
  #syncDialogEmptyState(itemCount) {
    const dialog = this.#dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    const inner = dialog.querySelector('.cart-drawer__inner');
    const hasContent =
      typeof itemCount === 'number'
        ? itemCount > 0
        : Boolean(dialog.querySelector('#cart-drawer-heading')) || this.#dialogHasRealLineItems(dialog);
    const isEmpty = !hasContent;
    const labelledBy = isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading';

    inner?.classList.toggle('cart-drawer--empty', isEmpty);
    if (dialog.getAttribute('aria-labelledby') !== labelledBy) {
      dialog.setAttribute('aria-labelledby', labelledBy);
    }
  }

  /**
   * @param {Element} root
   * @returns {boolean}
   */
  #dialogHasRealLineItems(root) {
    return root.querySelector('.cart-items__table-row:not([data-cart-item-skeleton])') !== null;
  }

  /** @param {Event} event */
  #handleEmptyStateSyncRequest = (event) => {
    const itemCount = /** @type {CustomEvent} */ (event).detail?.itemCount;
    this.#syncDialogEmptyState(itemCount);
  };

  #setupPendingStatusObserver() {
    const dialog = this.#dialog;
    if (!dialog) return;

    this.#pendingStatusObserver?.disconnect();
    this.#pendingStatusObserver = new MutationObserver(() => {
      if (!this.hasAttribute('data-cart-pending')) return;
      this.#syncPendingEmptyStatusMessage();
    });

    this.#pendingStatusObserver.observe(dialog, {
      childList: true,
      subtree: true,
    });

    this.#syncDialogEmptyState();
  }

  #handleLayoutSyncRequest = () => {
    this.#scheduleDrawerLayoutSync();
  };

  #scheduleDrawerLayoutSync() {
    if (this.#layoutSyncRafId !== null) return;

    this.#layoutSyncRafId = requestAnimationFrame(() => {
      this.#layoutSyncRafId = null;
      if (!this.#isDrawerOpen) return;

      this.#syncUpsellPlacement();

      const itemsHost = this.#dialog?.querySelector('.cart-drawer__items');
      this.#refreshScrollHints(itemsHost);

      this.#updateStickyState();
      this.#syncOffersCue();
    });
  }

  /**
   * Toggles the summary between sticky and static based on how much of the drawer
   * it occupies, so tall summaries scroll with the content instead of pinning.
   */
  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');

    if (content instanceof HTMLElement) {
      content.style.setProperty('--cart-drawer-summary-height', `${summaryHeight}px`);
    }
  }

  /**
   * Restores upsell to its home slot before a producer morphs the cart section. Producers
   * dispatch {@link CART_DRAWER_PRE_MORPH_EVENT} synchronously right before the morph, so this only
   * fires when a real section replacement is about to happen (never for lightweight/errored updates).
   */
  #handlePreMorph = () => {
    if (!this.#isDrawerOpen) return;
    this.#restoreUpsellHome();
  };

  /**
   * @param {HTMLDialogElement} dialog
   */
  #findUpsellElements(dialog) {
    const slot = dialog.querySelector('[data-cart-actions-slot]');
    const home = dialog.querySelector(CART_UPSELL_HOME_SELECTOR);
    const summaryUpsell =
      home instanceof HTMLElement ? home.querySelector(CART_UPSELL_SELECTOR) : null;
    const slotUpsell =
      slot instanceof HTMLElement ? slot.querySelector(CART_UPSELL_SELECTOR) : null;
    const upsell =
      summaryUpsell ??
      slotUpsell ??
      /** @type {HTMLElement | null} */ (dialog.querySelector(CART_UPSELL_SELECTOR));

    return { slot, home, summaryUpsell, slotUpsell, upsell };
  }

  #restoreUpsellHome() {
    const dialog = this.#dialog;
    if (!(dialog instanceof HTMLDialogElement) || !this.#isDrawerOpen) return;

    const { slot, home, upsell } = this.#findUpsellElements(dialog);
    if (!(upsell instanceof HTMLElement)) return;

    this.#teardownUpsellStaticPanel(upsell);
    upsell.classList.remove('cart-upsell--in-content');

    if (slot instanceof HTMLElement) {
      slot.hidden = true;
    }

    this.#hideOffersCue();

    if (!(home instanceof HTMLElement) || upsell.parentElement === home) return;

    home.appendChild(upsell);
  }

  #syncUpsellPlacement() {
    const dialog = this.#dialog;
    if (!(dialog instanceof HTMLDialogElement) || !this.#isDrawerOpen) return;

    let { slot, summaryUpsell, slotUpsell, upsell } = this.#findUpsellElements(dialog);

    // Prefer the summary copy if a morph left a stale duplicate in the slot.
    if (
      summaryUpsell instanceof HTMLElement &&
      slotUpsell instanceof HTMLElement &&
      summaryUpsell !== slotUpsell
    ) {
      slotUpsell.remove();
      upsell = summaryUpsell;
    }

    if (!(slot instanceof HTMLElement)) return;

    if (!(upsell instanceof HTMLElement)) {
      slot.hidden = true;
      this.#hideOffersCue();
      return;
    }

    const shouldBeInContent = mediaQueryCartDrawerShort.matches;
    const isInSlot = upsell.parentElement === slot;
    const upsellVisible = !upsell.hasAttribute('hidden');

    if (
      shouldBeInContent &&
      isInSlot &&
      upsell.classList.contains('cart-upsell--in-content') &&
      slot.hidden === !upsellVisible
    ) {
      return;
    }

    if (
      !shouldBeInContent &&
      !isInSlot &&
      !upsell.classList.contains('cart-upsell--in-content') &&
      slot.hidden
    ) {
      return;
    }

    if (shouldBeInContent) {
      if (!isInSlot) slot.appendChild(upsell);
      upsell.classList.add('cart-upsell--in-content');
      slot.hidden = !upsellVisible;
      this.#presentUpsellAsStaticPanel(upsell);
      return;
    }

    this.#restoreUpsellHome();
  }

  /**
   * In the short-viewport slot, keep the upsell expanded as a static panel:
   * label stays visible, collapse chrome is CSS-hidden, and toggle-closed is undone.
   * cart-upsell-component fetch/render is unchanged (still driven by details open).
   * @param {HTMLElement} upsell
   */
  #presentUpsellAsStaticPanel(upsell) {
    if (upsell.hasAttribute('hidden')) return;

    const details = upsell.querySelector('details');
    if (!(details instanceof HTMLDetailsElement)) return;

    if (!details.open) {
      details.open = true;
    }

    const summary = details.querySelector('summary');
    if (summary instanceof HTMLElement) {
      summary.tabIndex = -1;
      summary.setAttribute('aria-disabled', 'true');
    }

    if (this.#upsellStaticPanelAbortController) return;

    const abortController = new AbortController();
    this.#upsellStaticPanelAbortController = abortController;

    details.addEventListener(
      'toggle',
      () => {
        if (!upsell.classList.contains('cart-upsell--in-content')) return;
        if (!details.open) {
          details.open = true;
        }
      },
      { signal: abortController.signal }
    );
  }

  /**
   * Restores accordion interactivity when the upsell returns to the sticky summary.
   * @param {HTMLElement} upsell
   */
  #teardownUpsellStaticPanel(upsell) {
    this.#upsellStaticPanelAbortController?.abort();
    this.#upsellStaticPanelAbortController = null;

    const summary = upsell.querySelector('summary');
    if (summary instanceof HTMLElement) {
      summary.removeAttribute('tabindex');
      summary.removeAttribute('aria-disabled');
    }
  }

  #bindOffersCue() {
    const dialog = this.#dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.#offersCueAbortController?.abort();
    const abortController = new AbortController();
    this.#offersCueAbortController = abortController;
    const { signal } = abortController;

    const cue = dialog.querySelector('[data-offers-cue]');
    const itemsHost = dialog.querySelector('.cart-drawer__items');

    if (cue instanceof HTMLButtonElement) {
      cue.addEventListener('click', this.#handleOffersCueClick, { signal });
    }

    if (itemsHost instanceof HTMLElement) {
      itemsHost.addEventListener('scroll', this.#scheduleOffersCueSync, { passive: true, signal });
    }
  }

  #scheduleOffersCueSync = () => {
    if (this.#offersCueRafId !== null) return;

    this.#offersCueRafId = requestAnimationFrame(() => {
      this.#offersCueRafId = null;
      this.#syncOffersCue();
    });
  };

  #handleOffersCueClick = () => {
    const dialog = this.#dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    const { upsell } = this.#findUpsellElements(dialog);
    const itemsHost = dialog.querySelector('.cart-drawer__items');
    if (!(upsell instanceof HTMLElement) || !(itemsHost instanceof HTMLElement)) return;

    scrollIntoView(upsell, {
      ancestor: itemsHost,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'start',
    });
    this.#hideOffersCue();
  };

  #hideOffersCue() {
    const cue = this.#dialog?.querySelector('[data-offers-cue]');
    if (cue instanceof HTMLElement) {
      cue.hidden = true;
    }
  }

  /**
   * Shows the "Offers below" cue when the in-slot upsell is below the items scrollport.
   */
  #syncOffersCue() {
    const dialog = this.#dialog;
    if (!(dialog instanceof HTMLDialogElement) || !this.#isDrawerOpen) {
      this.#hideOffersCue();
      return;
    }

    const cue = dialog.querySelector('[data-offers-cue]');
    const itemsHost = dialog.querySelector('.cart-drawer__items');
    const { slot, upsell } = this.#findUpsellElements(dialog);

    if (
      !(cue instanceof HTMLElement) ||
      !(itemsHost instanceof HTMLElement) ||
      !(upsell instanceof HTMLElement) ||
      !(slot instanceof HTMLElement) ||
      upsell.parentElement !== slot ||
      upsell.hasAttribute('hidden') ||
      slot.hidden
    ) {
      this.#hideOffersCue();
      return;
    }

    const itemsRect = itemsHost.getBoundingClientRect();
    const upsellRect = upsell.getBoundingClientRect();
    const isBelowFold = upsellRect.top > itemsRect.bottom - 8;

    cue.hidden = !isBelowFold;
  }

  #disconnectLayoutTracking() {
    this.#layoutObserver?.disconnect();
    this.#layoutObserver = null;
    this.#viewportLayoutAbortController?.abort();
    this.#viewportLayoutAbortController = null;
    this.#offersCueAbortController?.abort();
    this.#offersCueAbortController = null;
    this.#upsellStaticPanelAbortController?.abort();
    this.#upsellStaticPanelAbortController = null;
    this.#debouncedViewportLayoutSync?.cancel();
    this.#debouncedViewportLayoutSync = null;

    if (this.#layoutSyncRafId !== null) {
      cancelAnimationFrame(this.#layoutSyncRafId);
      this.#layoutSyncRafId = null;
    }

    if (this.#offersCueRafId !== null) {
      cancelAnimationFrame(this.#offersCueRafId);
      this.#offersCueRafId = null;
    }
  }

  #bindViewportLayoutListeners() {
    if (!this.#isDrawerOpen) return;

    this.#viewportLayoutAbortController?.abort();
    const abortController = new AbortController();
    this.#viewportLayoutAbortController = abortController;
    const { signal } = abortController;

    this.#debouncedViewportLayoutSync?.cancel();
    this.#debouncedViewportLayoutSync = debounce(() => {
      this.#scheduleDrawerLayoutSync();
    }, 50);

    const handleViewportChange = this.#debouncedViewportLayoutSync;

    window.addEventListener('resize', handleViewportChange, { passive: true, signal });
    mediaQueryCartDrawerShort.addEventListener('change', handleViewportChange, { signal });

    const { visualViewport } = window;
    if (visualViewport) {
      visualViewport.addEventListener('resize', handleViewportChange, { passive: true, signal });
    }
  }

  /**
   * Syncs empty-state and drawer layout once a cart lines / discount update settles.
   * @param {{ didError?: boolean, lightweight?: boolean, itemCount?: number, resource?: { item_count?: number } } | undefined} detail
   */
  #applyLayoutAfterCartUpdate(detail) {
    const didError = Boolean(detail?.didError);
    const resource = /** @type {{ item_count?: number } | undefined} */ (detail?.resource);
    const itemCount = didError ? undefined : resource?.item_count ?? detail?.itemCount;

    // Producer morphSection may run in a microtask after resolve; defer until the DOM settles.
    queueMicrotask(() => {
      this.#syncDialogEmptyState(itemCount);
      if (this.#isDrawerOpen) {
        this.#scheduleDrawerLayoutSync();
      }
    });
  }

  /**
   * Recomputes scroll-hint masks after layout changes.
   * @param {Element | null | undefined} primary
   * @param {Element | null | undefined} [secondary]
   */
  #refreshScrollHints(primary, secondary = null) {
    for (const element of [primary, secondary]) {
      if (element instanceof HTMLElement) {
        element.dispatchEvent(new Event('scroll'));
      }
    }
  }

  #ensureLayoutObserver() {
    const dialog = this.#dialog;
    if (!dialog) return;

    if (this.#layoutObserver) {
      this.#scheduleDrawerLayoutSync();
      return;
    }

    const itemsHost = dialog.querySelector('.cart-drawer__items');
    const itemsHint = dialog.querySelector('.cart-drawer__items-hint');
    const wrapper = dialog.querySelector('.cart-items__wrapper');
    if (!(itemsHint instanceof HTMLElement) && !(wrapper instanceof HTMLElement)) return;

    this.#layoutObserver = new ResizeNotifier(() => {
      this.#scheduleDrawerLayoutSync();
    });

    if (itemsHint instanceof HTMLElement) {
      this.#layoutObserver.observe(itemsHint);
    }

    const rewardTier = dialog.querySelector('.cart-drawer__reward');
    if (rewardTier instanceof HTMLElement) {
      this.#layoutObserver.observe(rewardTier);
    }

    if (itemsHost instanceof HTMLElement) {
      this.#layoutObserver.observe(itemsHost);
    }

    if (wrapper instanceof HTMLElement) {
      this.#layoutObserver.observe(wrapper);
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
